// src/components/IndustryDetails.js
import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "../axiosConfig";
import "../styles/IndustryDetails.css";
import { useTranslation } from "react-i18next";

// Mapping country names to country codes
const countryMapping = {
  Austria: "at",
  Belgium: "be",
  Croatia: "hr",
  "Czech Republic": "cz",
  Denmark: "dk",
  Estonia: "ee",
  Finland: "fi",
  France: "fr",
  Germany: "de",
  Hungary: "hu",
  Italy: "it",
  Netherlands: "nl",
  Norway: "no",
  Poland: "pl",
  Portugal: "pt",
  Spain: "es",
  Sweden: "se",
  Switzerland: "ch",
  "United Kingdom": "gb",
};

// Mapping currency codes to flags
const currencyFlags = {
  EUR: "https://upload.wikimedia.org/wikipedia/commons/b/b7/Flag_of_Europe.svg",
  USD: "https://upload.wikimedia.org/wikipedia/en/a/a4/Flag_of_the_United_States.svg",
  GBP: "https://upload.wikimedia.org/wikipedia/en/a/a3/Flag_of_the_United_Kingdom.svg",
  CHF: "https://upload.wikimedia.org/wikipedia/commons/f/f3/Flag_of_Switzerland.svg",
  PLN: "https://upload.wikimedia.org/wikipedia/en/1/12/Flag_of_Poland.svg",
  NOK: "https://upload.wikimedia.org/wikipedia/commons/d/d9/Flag_of_Norway.svg",
  SEK: "https://upload.wikimedia.org/wikipedia/en/4/4c/Flag_of_Sweden.svg",
};

// Mapping phone number country codes to flags
const phoneFlags = {
  "+43": "https://flagcdn.com/w40/at.png",
  "+32": "https://flagcdn.com/w40/be.png",
  "+385": "https://flagcdn.com/w40/hr.png",
  "+420": "https://flagcdn.com/w40/cz.png",
  "+45": "https://flagcdn.com/w40/dk.png",
  "+372": "https://flagcdn.com/w40/ee.png",
  "+358": "https://flagcdn.com/w40/fi.png",
  "+33": "https://flagcdn.com/w40/fr.png",
  "+49": "https://flagcdn.com/w40/de.png",
  "+36": "https://flagcdn.com/w40/hu.png",
  "+39": "https://flagcdn.com/w40/it.png",
  "+31": "https://flagcdn.com/w40/nl.png",
  "+47": "https://flagcdn.com/w40/no.png",
  "+48": "https://flagcdn.com/w40/pl.png",
  "+351": "https://flagcdn.com/w40/pt.png",
  "+34": "https://flagcdn.com/w40/es.png",
  "+46": "https://flagcdn.com/w40/se.png",
  "+41": "https://flagcdn.com/w40/ch.png",
  "+44": "https://flagcdn.com/w40/gb.png",
};

function IndustryDetails() {
  const { t } = useTranslation("industry_details");
  const { industryId } = useParams();
  const navigate = useNavigate();
  const [industry, setIndustry] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    const fetchIndustryDetails = async () => {
      try {
        const response = await axios.get(`/industries/${industryId}`);
        setIndustry(response.data);
      } catch (error) {
        console.error("Error fetching industry details:", error);
        setErrorMessage(t("messages.fetch_error"));
      }
    };

    fetchIndustryDetails();
  }, [industryId, t]);

  const getCountryFlag = (countryName) => {
    if (!countryName) return "";
    const countryCode = countryMapping[countryName];
    return countryCode ? `https://flagcdn.com/w40/${countryCode.toLowerCase()}.png` : "";
  };

  const getCurrencyFlag = (currencyCode) => {
    if (!currencyCode) return "";
    return currencyFlags[currencyCode] || "";
  };

  const getPhoneFlag = (phoneCode) => {
    if (!phoneCode) return "";
    return phoneFlags[phoneCode] || "";
  };

  if (!industry) {
    return <div>{t("loading")}</div>;
  }

  const handleEdit = () => {
    // Envia todos os campos como prefill para render instantâneo no EditIndustry
    navigate(`/clients/edit/industry/${industryId}`, {
      state: {
        prefill: {
          companyName: industry.companyName ?? "",
          industryType: industry.industryType ?? "",
          otherIndustry: industry.otherIndustry ?? "",
          goodsProduced: industry.goodsProduced ?? "",
          transportationNeeds: industry.transportationNeeds ?? "",
          productionCapacity: industry.productionCapacity ?? "",
          currentTransportProviders: industry.currentTransportProviders ?? "",
          avgShippingVolumePerMonth: industry.avgShippingVolumePerMonth ?? "",
          frequentDestinations: industry.frequentDestinations ?? "",
          specialRequirements: industry.specialRequirements ?? "",
          address: industry.address ?? "",
          city: industry.city ?? "",
          postalCode: industry.postalCode ?? "",
          country: industry.country ?? "",
          mainContactName: industry.mainContactName ?? "",
          mainContactTitle: industry.mainContactTitle ?? "",
          contactEmail: industry.contactEmail ?? "",
          phoneNumberCountryCode: industry.phoneNumberCountryCode ?? "",
          phoneNumber: industry.phoneNumber ?? "",
          website: industry.website ?? "",
          bankName: industry.bankName ?? "",
          accountHolderName: industry.accountHolderName ?? "",
          accountNumber: industry.accountNumber ?? "",
          IBAN: industry.IBAN ?? "",
          SWIFT_BIC: industry.SWIFT_BIC ?? "",
          currency: industry.currency ?? "",
          paymentMethodsAccepted: industry.paymentMethodsAccepted ?? [],
          paymentDueDate: industry.paymentDueDate ?? "",
          discountTerms: industry.discountTerms ?? "",
          latePaymentPenalties: industry.latePaymentPenalties ?? "",
          VAT_TaxInformation: industry.VAT_TaxInformation ?? "",
          invoiceDeliveryMethod: industry.invoiceDeliveryMethod ?? "",
          additionalNotes: industry.additionalNotes ?? "",
        },
      },
    });
  };

  const NS = (v) => v || t("not_specified");

  return (
    <div className="industry-details-container">
      <div
        className="industry-details-back-arrow"
        onClick={() => navigate("/clients?view=industries")}
      >
        {t("back")}
      </div>
      {errorMessage && <p style={{ color: "red" }}>{errorMessage}</p>}

      <h2 className="industry-details-section-title-1">{t("title")}</h2>
      <div className="industry-details-columns">
        {/* Basic Information */}
        <div className="industry-details-column">
          <div className="industry-details-section-title">{t("section.basic")}</div>
          <div className="industry-details-field-row">
            <span className="industry-details-label">{t("fields.company_name")}</span>
            <span className="industry-details-value">{NS(industry.companyName)}</span>
          </div>
          <div className="industry-details-field-row">
            <span className="industry-details-label">{t("fields.industry_type")}</span>
            <span className="industry-details-value">{NS(industry.industryType)}</span>
          </div>
          {industry.industryType === "Other" && (
            <div className="industry-details-field-row">
              <span className="industry-details-label">{t("fields.specify_industry")}</span>
              <span className="industry-details-value">{NS(industry.otherIndustry)}</span>
            </div>
          )}
          <div className="industry-details-field-row">
            <span className="industry-details-label">{t("fields.goods_produced")}</span>
            <span className="industry-details-value">{NS(industry.goodsProduced)}</span>
          </div>
          <div className="industry-details-field-row">
            <span className="industry-details-label">{t("fields.transportation_needs")}</span>
            <span className="industry-details-value">{NS(industry.transportationNeeds)}</span>
          </div>
          <div className="industry-details-field-row">
            <span className="industry-details-label">{t("fields.production_capacity")}</span>
            <span className="industry-details-value">{NS(industry.productionCapacity)}</span>
          </div>
          <div className="industry-details-field-row">
            <span className="industry-details-label">{t("fields.current_transport_providers")}</span>
            <span className="industry-details-value">{NS(industry.currentTransportProviders)}</span>
          </div>
          <div className="industry-details-field-row">
            <span className="industry-details-label">{t("fields.avg_shipping_volume_per_month")}</span>
            <span className="industry-details-value">{NS(industry.avgShippingVolumePerMonth)}</span>
          </div>
          <div className="industry-details-field-row">
            <span className="industry-details-label">{t("fields.frequent_destinations")}</span>
            <span className="industry-details-value">{NS(industry.frequentDestinations)}</span>
          </div>
          <div className="industry-details-field-row">
            <span className="industry-details-label">{t("fields.special_requirements")}</span>
            <span className="industry-details-value">{NS(industry.specialRequirements)}</span>
          </div>
        </div>

        {/* Address & Contact Information */}
        <div className="industry-details-column">
          <div className="industry-details-section-title">{t("section.address_contact")}</div>
          <div className="industry-details-field-row">
            <span className="industry-details-label">{t("fields.address")}</span>
            <span className="industry-details-value">{NS(industry.address)}</span>
          </div>
          <div className="industry-details-field-row">
            <span className="industry-details-label">{t("fields.city")}</span>
            <span className="industry-details-value">{NS(industry.city)}</span>
          </div>
          <div className="industry-details-field-row">
            <span className="industry-details-label">{t("fields.postal_code")}</span>
            <span className="industry-details-value">{NS(industry.postalCode)}</span>
          </div>
          <div className="industry-details-field-row">
            <span className="industry-details-label">{t("fields.country")}</span>
            <span className="industry-details-value">
              {NS(industry.country)}
              {industry.country && (
                <img
                  src={getCountryFlag(industry.country)}
                  alt="Country Flag"
                  className="industry-details-flag"
                />
              )}
            </span>
          </div>
          <div className="industry-details-field-row">
            <span className="industry-details-label">{t("fields.main_contact_name")}</span>
            <span className="industry-details-value">{NS(industry.mainContactName)}</span>
          </div>
          <div className="industry-details-field-row">
            <span className="industry-details-label">{t("fields.main_contact_title")}</span>
            <span className="industry-details-value">{NS(industry.mainContactTitle)}</span>
          </div>
          <div className="industry-details-field-row">
            <span className="industry-details-label">{t("fields.contact_email")}</span>
            <span className="industry-details-value">{NS(industry.contactEmail)}</span>
          </div>
          <div className="industry-details-field-row">
            <span className="industry-details-label">{t("fields.phone_cc")}</span>
            <span className="industry-details-value">
              {NS(industry.phoneNumberCountryCode)}
              {industry.phoneNumberCountryCode && (
                <img
                  src={getPhoneFlag(industry.phoneNumberCountryCode)}
                  alt="Phone Code Flag"
                  className="industry-details-flag"
                />
              )}
            </span>
          </div>
          <div className="industry-details-field-row">
            <span className="industry-details-label">{t("fields.phone_number")}</span>
            <span className="industry-details-value">{NS(industry.phoneNumber)}</span>
          </div>
          <div className="industry-details-field-row">
            <span className="industry-details-label">{t("fields.website")}</span>
            <span className="industry-details-value">{NS(industry.website)}</span>
          </div>
        </div>

        {/* Bank & Payment Details */}
        <div className="industry-details-column">
          <div className="industry-details-section-title">{t("section.bank_payment")}</div>
          <div className="industry-details-field-row">
            <span className="industry-details-label">{t("fields.bank_name")}</span>
            <span className="industry-details-value">{NS(industry.bankName)}</span>
          </div>
          <div className="industry-details-field-row">
            <span className="industry-details-label">{t("fields.account_holder_name")}</span>
            <span className="industry-details-value">{NS(industry.accountHolderName)}</span>
          </div>
          <div className="industry-details-field-row">
            <span className="industry-details-label">{t("fields.account_number")}</span>
            <span className="industry-details-value">{NS(industry.accountNumber)}</span>
          </div>
          <div className="industry-details-field-row">
            <span className="industry-details-label">{t("fields.iban")}</span>
            <span className="industry-details-value">{NS(industry.IBAN)}</span>
          </div>
          <div className="industry-details-field-row">
            <span className="industry-details-label">{t("fields.swift_bic")}</span>
            <span className="industry-details-value">{NS(industry.SWIFT_BIC)}</span>
          </div>
          <div className="industry-details-field-row">
            <span className="industry-details-label">{t("fields.currency")}</span>
            <span className="industry-details-value">
              {NS(industry.currency)}
              {industry.currency && (
                <img
                  src={getCurrencyFlag(industry.currency)}
                  alt="Currency Flag"
                  className="industry-details-flag"
                />
              )}
            </span>
          </div>
          <div className="industry-details-field-row">
            <span className="industry-details-label">{t("fields.payment_methods_accepted")}</span>
            <span className="industry-details-value">{NS(industry.paymentMethodsAccepted)}</span>
          </div>
          <div className="industry-details-field-row">
            <span className="industry-details-label">{t("fields.payment_due_date")}</span>
            <span className="industry-details-value">{NS(industry.paymentDueDate)}</span>
          </div>
          <div className="industry-details-field-row">
            <span className="industry-details-label">{t("fields.discount_terms")}</span>
            <span className="industry-details-value">{NS(industry.discountTerms)}</span>
          </div>
          <div className="industry-details-field-row">
            <span className="industry-details-label">{t("fields.late_payment_penalties")}</span>
            <span className="industry-details-value">{NS(industry.latePaymentPenalties)}</span>
          </div>

          <div className="industry-details-section-title" style={{ marginTop: 20 }}>
            {t("section.invoicing")}
          </div>
          <div className="industry-details-field-row">
            <span className="industry-details-label">{t("fields.vat_tax_information")}</span>
            <span className="industry-details-value">{NS(industry.VAT_TaxInformation)}</span>
          </div>
          <div className="industry-details-field-row">
            <span className="industry-details-label">{t("fields.invoice_delivery_method")}</span>
            <span className="industry-details-value">{NS(industry.invoiceDeliveryMethod)}</span>
          </div>
        </div>
      </div>

      <button className="industry-details-edit-btn" onClick={handleEdit}>
        {t("buttons.edit_industry")}
      </button>
    </div>
  );
}

export default IndustryDetails;
